import React, { useContext, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AddIcon from "@mui/icons-material/Add";
import { useQueryClient } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { useParams } from "react-router-dom";

import { taskListColumns } from "./columns";
import TimesheetsDialog from "./TimesheetsDialog";

import { AuthContext } from "../../../../../../context/authContext";
import { EditableTable } from "../../../../../../components/Tables/NewEditableTable";
import CommentsDialog from "../../../../../../components/CommentsDialog";
import ColoredSnackbar from "../../../../../../components/ColoredSnakebar";

import { useSchedule4D } from "../../../../AllEquipments/CommonFeatures/MCS3/Schedule4D/Schedule4DContext";
import { useDCS } from "../../../../AllEquipments/CommonFeatures/DCSTab/Table/DCSContext";
import {
  resolveScheduleTaskType,
} from "./components/constants";
import { createTask } from "./api";
import CreateTaskDialog from "../../../../AllEquipments/CommonFeatures/PMSTab2/4d/CreateTaskDialog";
import { useSiToggle } from "../../../../AllEquipments/CommonFeatures/MCS3/Schedule4D/useSiToggle";
import { useScheduleTaskData } from "./components/useScheduleTaskData";
import { useTaskTableHandlers } from "./components/useTaskTableHandlers";
import { useDerivedTaskData } from "./components/useDerivedTaskData";
import { use4DTaskSync } from "./components/use4DTaskSync";

import TaskFilters from "./components/TaskFilters";
import TaskStats from "./components/TaskStats";
import TaskLoadingSkeleton from "./components/TaskLoadingSkeleton";
import ResourcesModal from "../../../../AllEquipments/CommonFeatures/Resources/ResourcesModal";
import { useResources } from "../../../../AllEquipments/CommonFeatures/Resources/useResources";

export default function TaskList({
  selectedPlant,
  type: fixedType,
  mrq,
  materials,
  isPCSTab,
  itpTable = [],
}) {
  const queryClient = useQueryClient();
  const { accessToken, user } = useContext(AuthContext);
  const { plantId } = useParams();
  const { accounts } = useMsal();
  const username = accounts[0]?.name;
  const schedule4D = useSchedule4D();
  const documentList = useDCS()?.documentList ?? [];

  const initialDialogState = {
    comments: false,
    attachment: false,
    timesheet: false,
    resources: false,
  };

  const [dialogState, setDialogState] = useState(initialDialogState);
  const [selectedTask, setSelectedTask] = useState();
  const [filterType, setFilterType] = useState("");
  const [commentsModalState, setCommentsDialogState] = useState(false);
  const [clickedProject, setClickedProject] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { resources, invalidate: invalidateResources } = useResources();

  const last4DSyncBatchKeyRef = useRef("");
  const isSyncing4DRef = useRef(false);

  const scheduleTaskType = resolveScheduleTaskType(fixedType);

  const { scheduleTasks, allTasks, tasksLoading, loadScheduleTasks, patchTaskSi } =
    useScheduleTaskData({
      selectedPlant,
      accessToken,
      schedule4D,
      setSnackbar,
      fixedType,
    });

  const {
    filteredTasks,
    tasksToSync4D,
    totalActualHours,
    averageProgress,
  } = useDerivedTaskData({
    scheduleTasks,
    allTasks,
    itpTable,
    filterType,
    fixedType,
  });

  use4DTaskSync({
    tasksToSync4D,
    accessToken,
    isSyncing4DRef,
    last4DSyncBatchKeyRef,
  });


  const { hooks, actions } = useTaskTableHandlers({
    scheduleTasks,
    tasksLoading,
    loadScheduleTasks,
    setSnackbar,
  });

  const { handleSiChange, siUpdatingIds } = useSiToggle({
    accessToken,
    onUpdated: (taskId, si) => patchTaskSi(taskId, si),
  });

  const handleButtonClick = (type, row) => {
    setSelectedTask(row.original);
    queryClient.setQueryData(["taskId"], row.id, { staleTime: Infinity });

    if (type === "comments") {
      setDialogState((prev) => ({ ...prev, comments: true }));
    } else if (type === "attachment") {
      setDialogState((prev) => ({ ...prev, attachment: true }));
    } else if (type === "timesheet") {
      setDialogState((prev) => ({ ...prev, timesheet: true }));
    } else if (type === "resources") {
      setDialogState((prev) => ({ ...prev, resources: true }));
    }
  };

  const handleDialogClose = () => {
    setDialogState(initialDialogState);
  };

  const handleCommentDialogOpen = (row) => {
    setClickedProject(row.original.id);
    setCommentsDialogState(true);
  };

  const additionalProps = {
    enableSorting: true,
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.95rem",
        fontWeight: "bold",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        backgroundColor: "rgba(0,0,0,0)",
        width: "100%",
      },
    },
  };

  const isAdmin = true;
  const plantPeople = selectedPlant?.people || [];
  const plantLeads = [];

  return (
    <Box
      sx={{
        width: "100%",
        typography: "body1",
        overflow: "auto",
        margin: "16px 0",
      }}
    >
      {!tasksLoading ? (
        <>
          {fixedType !== "fabrication" && (
            <Typography variant="h5">{selectedPlant?.name}</Typography>
          )}

          <TaskFilters filterType={filterType} onChange={setFilterType} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
            {isAdmin && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Task
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ManageAccountsIcon />}
              onClick={() => {
                setSelectedTask(null);
                setDialogState((prev) => ({ ...prev, resources: true }));
              }}
            >
              Manage Resources
            </Button>
          </Box>

          <TaskStats
            totalActualHours={totalActualHours}
            averageProgress={averageProgress}
          />

          <EditableTable
            columns={taskListColumns(
              plantPeople,
              handleButtonClick,
              username,
              plantLeads,
              handleCommentDialogOpen,
              filteredTasks,
              fixedType,
              mrq,
              materials?.map((m) => m?.description) || [],
              isPCSTab,
              documentList,
              resources,
              handleSiChange,
              siUpdatingIds,
            )}
            name="Task"
            actions={actions}
            hooks={hooks}
            editMode="row"
            enableEditing={isAdmin ? true : false}
            enableArchive={true}
            additionalProps={additionalProps}
            data={Array.isArray(filteredTasks) ? filteredTasks : []}
            accessToken={accessToken}
          />
        </>
      ) : (
        <TaskLoadingSkeleton />
      )}

      <CommentsDialog
        open={commentsModalState}
        handleClose={() => {
          setClickedProject("");
          setCommentsDialogState(false);
        }}
        parentId={clickedProject}
        parentType="task"
      />

      <TimesheetsDialog
        open={dialogState.timesheet}
        handleClose={handleDialogClose}
        task={selectedTask}
      />

      <ResourcesModal
        open={dialogState.resources}
        onClose={handleDialogClose}
        task={selectedTask}
        onResourcesChange={invalidateResources}
      />

      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        tasks={allTasks}
        defaultType={scheduleTaskType}
        lockType={Boolean(fixedType)}
        plant
        plantId={plantId}
        accessToken={accessToken}
        user={user}
        resources={resources}
        title="Create Task"
        onSubmit={async (payload) => {
          await createTask({ task: payload, plantId, accessToken });
        }}
        onCreated={async () => {
          await loadScheduleTasks();
          setSnackbar({
            open: true,
            message: "Task created successfully!",
            severity: "success",
          });
        }}
      />

      <ColoredSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
